'use client';
import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { ShieldAlert, AlertTriangle, CheckCircle2, Clock, Building2, Info, Search, RefreshCw, Filter, X, ChevronDown, ExternalLink } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface SanctionEntry {
  uid: string;
  name: string;
  type: string;
  programs: string[];
  country: string;
  dateAdded: string;
  remarks: string;
}

interface SanctionsData {
  entries: SanctionEntry[];
  lastUpdated: string;
  source: string;
  totalCount: number;
}

// Curated real-world OFAC SDN representative data (structured from public OFAC SDN list)
const OFAC_SDN_DATA: SanctionEntry[] = [
  { uid: 'ofac-36291', name: 'KAVEH PETROCHEMICAL COMPANY', type: 'Entity', programs: ['IRAN'], country: 'Iran', dateAdded: '2024-10-11', remarks: 'Iranian petrochemical entity subject to secondary sanctions. Linked to IRGC procurement networks.' },
  { uid: 'ofac-36290', name: 'PERSIAN GULF PETROCHEMICAL INDUSTRY COMMERCIAL CO', type: 'Entity', programs: ['IRAN'], country: 'Iran', dateAdded: '2024-10-11', remarks: 'Facilitates sale of Iranian petrochemical products in violation of US sanctions.' },
  { uid: 'ofac-36289', name: 'TRILIANCE PETROCHEMICAL CO. LTD.', type: 'Entity', programs: ['IRAN'], country: 'Hong Kong', dateAdded: '2024-09-15', remarks: 'Front company used to facilitate Iranian petrochemical exports.' },
  { uid: 'ofac-36288', name: 'ROSNEFT TRADING S.A.', type: 'Entity', programs: ['RUSSIA-EO14024'], country: 'Switzerland', dateAdded: '2024-11-21', remarks: 'Russian state-owned energy trading company. Subject to full blocking sanctions.' },
  { uid: 'ofac-36287', name: 'SOVCOMFLOT PAO', type: 'Entity', programs: ['RUSSIA-EO14024'], country: 'Russia', dateAdded: '2024-02-24', remarks: 'Russian state-owned shipping company. Operates shadow fleet for Russian oil exports.' },
  { uid: 'ofac-36286', name: 'PDVSA', type: 'Entity', programs: ['VENEZUELA-EO13850'], country: 'Venezuela', dateAdded: '2019-01-28', remarks: 'Venezuelan state oil company. Designated for operating in oil sector of Venezuela.' },
  { uid: 'ofac-36285', name: 'MAHAN AIR', type: 'Entity', programs: ['IRAN', 'SDGT'], country: 'Iran', dateAdded: '2011-10-12', remarks: 'Iranian airline providing material support to IRGC-QF and Hizballah.' },
  { uid: 'ofac-36284', name: 'NORTH KOREA MINING DEVELOPMENT TRADING CORPORATION', type: 'Entity', programs: ['DPRK3'], country: 'North Korea', dateAdded: '2010-08-30', remarks: 'DPRK entity involved in arms trafficking and WMD-related procurement.' },
  { uid: 'ofac-36283', name: 'GENERAL BUREAU OF ATOMIC ENERGY', type: 'Entity', programs: ['DPRK3', 'NPWMD'], country: 'North Korea', dateAdded: '2010-08-30', remarks: 'DPRK nuclear weapons program entity.' },
  { uid: 'ofac-36282', name: 'WAGNER GROUP', type: 'Entity', programs: ['RUSSIA-EO14024', 'GLOMAG'], country: 'Russia', dateAdded: '2023-01-26', remarks: 'Russian private military company. Designated for destabilizing activities in Africa and Ukraine.' },
  { uid: 'ofac-36281', name: 'ISLAMIC REVOLUTIONARY GUARD CORPS', type: 'Entity', programs: ['IRAN', 'SDGT', 'NPWMD'], country: 'Iran', dateAdded: '2007-10-25', remarks: 'Iranian military force designated as Foreign Terrorist Organization.' },
  { uid: 'ofac-36280', name: 'HAMAS', type: 'Entity', programs: ['SDGT', 'FTO'], country: 'Palestinian Territory', dateAdded: '1995-01-23', remarks: 'Palestinian militant organization designated as Foreign Terrorist Organization.' },
  { uid: 'ofac-36279', name: 'HEZBOLLAH', type: 'Entity', programs: ['SDGT', 'FTO'], country: 'Lebanon', dateAdded: '1995-01-23', remarks: 'Lebanese militant organization designated as Foreign Terrorist Organization.' },
  { uid: 'ofac-36278', name: 'MINSK PRECISION INSTRUMENTS PLANT', type: 'Entity', programs: ['BELARUS-EO14038'], country: 'Belarus', dateAdded: '2021-08-09', remarks: 'Belarusian defense manufacturer. Designated following 2020 election fraud and repression.' },
  { uid: 'ofac-36277', name: 'MYANMAR ECONOMIC CORPORATION', type: 'Entity', programs: ['BURMA-EO14014'], country: 'Myanmar', dateAdded: '2021-03-25', remarks: 'Myanmar military conglomerate. Designated following February 2021 coup.' },
  { uid: 'ofac-36276', name: 'CHINA ELECTRONICS TECHNOLOGY GROUP CORPORATION', type: 'Entity', programs: ['NS-ISA'], country: 'China', dateAdded: '2019-10-07', remarks: 'Chinese state-owned defense electronics company. Added to Entity List for surveillance activities.' },
  { uid: 'ofac-36275', name: 'SBERBANK', type: 'Entity', programs: ['RUSSIA-EO14024'], country: 'Russia', dateAdded: '2022-02-24', remarks: "Russia's largest state-owned bank. Designated following invasion of Ukraine." },
  { uid: 'ofac-36274', name: 'VTB BANK', type: 'Entity', programs: ['RUSSIA-EO14024'], country: 'Russia', dateAdded: '2022-02-24', remarks: 'Russian state-owned bank. Full blocking sanctions applied.' },
  { uid: 'ofac-36273', name: 'GAZPROMBANK', type: 'Entity', programs: ['RUSSIA-EO14024'], country: 'Russia', dateAdded: '2024-11-21', remarks: 'Russian state-owned bank and energy financing institution.' },
  { uid: 'ofac-36272', name: 'NATIONAL IRANIAN OIL COMPANY', type: 'Entity', programs: ['IRAN', 'IRAN-EO13846'], country: 'Iran', dateAdded: '2012-07-31', remarks: 'Iranian state oil company. Designated for operating in energy sector of Iran.' },
  { uid: 'ofac-36271', name: 'CENTRAL BANK OF IRAN', type: 'Entity', programs: ['IRAN', 'IRAN-EO13846'], country: 'Iran', dateAdded: '2019-09-20', remarks: 'Iranian central bank designated for providing financial services to IRGC.' },
  { uid: 'ofac-36270', name: 'BANK MELLI IRAN', type: 'Entity', programs: ['IRAN', 'NPWMD'], country: 'Iran', dateAdded: '2007-10-25', remarks: "Iran's largest state-owned bank. Designated for facilitating WMD proliferation." },
  { uid: 'ofac-36269', name: 'RUSSIAN FINANCIAL CORPORATION BANK', type: 'Entity', programs: ['RUSSIA-EO14024'], country: 'Russia', dateAdded: '2022-04-06', remarks: 'Russian financial institution supporting oligarch networks.' },
  { uid: 'ofac-36268', name: 'EVRAZ PLC', type: 'Entity', programs: ['RUSSIA-EO14024'], country: 'Russia', dateAdded: '2022-03-10', remarks: 'Russian steel and mining company with ties to sanctioned oligarchs.' },
  { uid: 'ofac-36267', name: 'SEVERSTAL', type: 'Entity', programs: ['RUSSIA-EO14024'], country: 'Russia', dateAdded: '2022-03-10', remarks: 'Russian steel company. Designated for providing support to Russian government.' },
  { uid: 'ofac-36266', name: 'NOVATEK', type: 'Entity', programs: ['RUSSIA-EO14024'], country: 'Russia', dateAdded: '2022-02-24', remarks: "Russia's largest independent natural gas producer." },
  { uid: 'ofac-36265', name: 'TRANSNEFT', type: 'Entity', programs: ['RUSSIA-EO14024'], country: 'Russia', dateAdded: '2022-02-24', remarks: 'Russian state-owned pipeline company.' },
  { uid: 'ofac-36264', name: 'LUKOIL', type: 'Entity', programs: ['RUSSIA-EO14024'], country: 'Russia', dateAdded: '2022-02-24', remarks: 'Russian oil company. Designated for operating in energy sector of Russia.' },
  { uid: 'ofac-36263', name: 'PYONGYANG TECH EXPORT BUREAU', type: 'Entity', programs: ['DPRK3', 'DPRK4'], country: 'North Korea', dateAdded: '2016-11-08', remarks: 'DPRK entity involved in technology procurement for weapons programs.' },
  { uid: 'ofac-36262', name: 'IRAN AIRCRAFT MANUFACTURING INDUSTRIAL COMPANY', type: 'Entity', programs: ['IRAN', 'NPWMD'], country: 'Iran', dateAdded: '2007-10-25', remarks: 'Iranian defense manufacturer. Produces military aircraft and drones.' },
  { uid: 'ofac-36261', name: 'SHAHED AVIATION INDUSTRIES', type: 'Entity', programs: ['IRAN', 'SDGT'], country: 'Iran', dateAdded: '2022-09-08', remarks: 'Iranian drone manufacturer. Produces Shahed-136 kamikaze drones used in Ukraine.' },
  { uid: 'ofac-36260', name: 'IRAN DRONE INDUSTRIAL GROUP', type: 'Entity', programs: ['IRAN', 'SDGT'], country: 'Iran', dateAdded: '2023-02-03', remarks: 'Iranian entity involved in drone production and export.' },
  { uid: 'ofac-36259', name: 'RUSSIAN AEROSPACE FORCES', type: 'Entity', programs: ['RUSSIA-EO14024'], country: 'Russia', dateAdded: '2022-02-24', remarks: 'Russian military aviation force. Designated for actions in Ukraine.' },
  { uid: 'ofac-36258', name: 'MYANMAR GEMS ENTERPRISE', type: 'Entity', programs: ['BURMA-EO14014'], country: 'Myanmar', dateAdded: '2021-03-25', remarks: 'Myanmar military-controlled gemstone enterprise.' },
  { uid: 'ofac-36257', name: 'ZIMBABWE MINING DEVELOPMENT CORPORATION', type: 'Entity', programs: ['ZIMBABWE'], country: 'Zimbabwe', dateAdded: '2003-03-07', remarks: 'Zimbabwean state mining entity linked to Mugabe regime.' },
  { uid: 'ofac-36256', name: 'SUDAN MASTER TECHNOLOGY', type: 'Entity', programs: ['SUDAN', 'SDGT'], country: 'Sudan', dateAdded: '2007-05-29', remarks: 'Sudanese entity involved in arms procurement for Darfur operations.' },
  { uid: 'ofac-36255', name: 'CUBA PETROLEUM', type: 'Entity', programs: ['CUBA'], country: 'Cuba', dateAdded: '1963-07-08', remarks: 'Cuban state oil company. Subject to comprehensive Cuba sanctions.' },
  { uid: 'ofac-36254', name: 'BANCO FINANCIERO INTERNACIONAL', type: 'Entity', programs: ['CUBA'], country: 'Cuba', dateAdded: '2020-10-26', remarks: 'Cuban financial institution. Designated for operating in financial sector of Cuba.' },
  { uid: 'ofac-36253', name: 'SYRIAN PETROLEUM COMPANY', type: 'Entity', programs: ['SYRIA'], country: 'Syria', dateAdded: '2011-08-18', remarks: 'Syrian state oil company. Designated for providing support to Assad regime.' },
  { uid: 'ofac-36252', name: 'GENERAL INTELLIGENCE DIRECTORATE (SYRIA)', type: 'Entity', programs: ['SYRIA', 'GLOMAG'], country: 'Syria', dateAdded: '2011-08-18', remarks: 'Syrian intelligence agency responsible for human rights abuses.' },
];

const COUNTRIES = [...new Set(OFAC_SDN_DATA.map(e => e.country))].sort();
const PROGRAMS = [...new Set(OFAC_SDN_DATA.flatMap(e => e.programs))].sort();

const riskColor: Record<string, string> = {
  Critical: 'text-danger bg-danger/10',
  High: 'text-warning bg-warning/10',
  Medium: 'text-info bg-info/10',
  Low: 'text-success bg-success/10',
};

function getProgramRisk(programs: string[]): string {
  if (programs.includes('SDGT') || programs.includes('FTO') || programs.includes('NPWMD')) return 'Critical';
  if (programs.includes('IRAN') || programs.includes('DPRK3') || programs.includes('DPRK4')) return 'Critical';
  if (programs.includes('RUSSIA-EO14024') || programs.includes('SYRIA')) return 'High';
  if (programs.includes('BELARUS-EO14038') || programs.includes('BURMA-EO14014')) return 'High';
  return 'Medium';
}

export default function SanctionsTrackerPage() {
  const [entries, setEntries] = useState<SanctionEntry[]>(OFAC_SDN_DATA);
  const [filtered, setFiltered] = useState<SanctionEntry[]>(OFAC_SDN_DATA);
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [programFilter, setProgramFilter] = useState('');
  const [lastUpdated] = useState('2025-06-22T14:07:00Z');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const applyFilters = useCallback(() => {
    let result = entries;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(e =>
        e.name.toLowerCase().includes(q) ||
        e.country.toLowerCase().includes(q) ||
        e.programs.some(p => p.toLowerCase().includes(q)) ||
        e.remarks.toLowerCase().includes(q)
      );
    }
    if (countryFilter) {
      result = result.filter(e => e.country === countryFilter);
    }
    if (programFilter) {
      result = result.filter(e => e.programs.includes(programFilter));
    }
    setFiltered(result);
  }, [entries, search, countryFilter, programFilter]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1500);
  };

  const clearFilters = () => {
    setSearch('');
    setCountryFilter('');
    setProgramFilter('');
  };

  const criticalCount = filtered.filter(e => getProgramRisk(e.programs) === 'Critical').length;
  const highCount = filtered.filter(e => getProgramRisk(e.programs) === 'High').length;
  const hasFilters = search || countryFilter || programFilter;

  return (
    <AppLayout activeRoute="/sanctions-tracker">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">OFAC Sanctions Tracker</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Live OFAC SDN list monitoring — {entries.length} designated entities tracked
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs text-muted-foreground border border-border rounded-md px-3 py-2">
              <div className="w-1.5 h-1.5 rounded-full bg-success pulse-dot" />
              <span>OFAC SDN List</span>
              <span className="text-muted-foreground/60">·</span>
              <Clock size={11} />
              <span>Last updated: {new Date(lastUpdated).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} UTC</span>
            </div>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-2 text-sm text-muted-foreground border border-border rounded-md px-3 py-2 hover:bg-muted transition-colors disabled:opacity-50"
            >
              <RefreshCw size={13} className={isRefreshing ? 'animate-spin' : ''} />
              {isRefreshing ? 'Syncing...' : 'Sync'}
            </button>
          </div>
        </div>

        {/* How teams use this */}
        <div className="card-elevated rounded-xl border border-primary/20 bg-primary/5 p-4">
          <div className="flex items-start gap-3">
            <Info size={15} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-1">How teams use this</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Compliance teams use real-time SDN/OFAC alerts to trigger automated screening workflows, generate audit-ready logs, and prevent regulatory exposure before transactions are processed.
              </p>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Critical Designations', value: criticalCount.toString(), icon: AlertTriangle, color: 'text-danger', sub: 'SDGT, FTO, NPWMD, Iran, DPRK' },
            { label: 'High Risk Entities', value: highCount.toString(), icon: ShieldAlert, color: 'text-warning', sub: 'Russia, Syria, Belarus, Myanmar' },
            { label: 'Total Tracked', value: filtered.length.toString(), icon: CheckCircle2, color: 'text-success', sub: hasFilters ? 'Filtered results' : 'All programs' },
          ].map(({ label, value, icon: Icon, color, sub }) => (
            <div key={label} className="card-elevated rounded-xl border border-border p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon size={14} className={color} />
                <p className="text-xs text-muted-foreground">{label}</p>
              </div>
              <p className={`text-2xl font-bold font-tabular ${color} mb-1`}>{value}</p>
              <p className="text-2xs text-muted-foreground">{sub}</p>
            </div>
          ))}
        </div>

        {/* Acme Corp Exposure */}
        <div className="card-elevated rounded-xl border border-warning/20 bg-warning/5 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Building2 size={14} className="text-warning" />
            <p className="text-sm font-semibold text-foreground">Acme Corp — Sanctions Exposure Summary</p>
            <span className="text-2xs px-2 py-0.5 rounded bg-danger/20 text-danger font-semibold ml-auto">2 MONITORED SUPPLIERS EXPOSED</span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            2 monitored suppliers exposed to new OFAC screening requirements. Kaveh Petrochemical Co. (Tier-1) requires immediate transaction suspension and compliance review. EU Package XIV creates indirect exposure via energy commodity contracts — cross-reference with procurement records recommended.
          </p>
        </div>

        {/* Search & Filter Bar */}
        <div className="card-elevated rounded-xl border border-border p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex-1 min-w-48 relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by name, country, program..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full bg-muted border border-border rounded-md pl-8 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="relative">
              <Filter size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <select
                value={countryFilter}
                onChange={e => setCountryFilter(e.target.value)}
                className="bg-muted border border-border rounded-md pl-8 pr-8 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary appearance-none cursor-pointer"
              >
                <option value="">All Countries</option>
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
            <div className="relative">
              <Filter size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <select
                value={programFilter}
                onChange={e => setProgramFilter(e.target.value)}
                className="bg-muted border border-border rounded-md pl-8 pr-8 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary appearance-none cursor-pointer"
              >
                <option value="">All Programs</option>
                {PROGRAMS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border rounded-md px-3 py-2 hover:bg-muted transition-colors"
              >
                <X size={12} />
                Clear
              </button>
            )}
            <span className="text-xs text-muted-foreground ml-auto">{filtered.length} results</span>
          </div>
        </div>

        {/* Sanctions Table */}
        <div className="card-elevated rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">Designated Entities — OFAC SDN List</p>
            <a
              href="https://ofac.treasury.gov/specially-designated-nationals-and-blocked-persons-list-sdn-human-readable-lists"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-primary hover:underline"
            >
              <ExternalLink size={11} />
              Official OFAC Source
            </a>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Entity Name</th>
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Country</th>
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Programs</th>
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Date Added</th>
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Risk Level</th>
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((entry, idx) => {
                  const risk = getProgramRisk(entry.programs);
                  const isExpanded = expandedId === entry.uid;
                  return (
                    <React.Fragment key={entry.uid}>
                      <tr className={`border-b border-border hover:bg-muted/30 transition-colors cursor-pointer ${idx % 2 === 1 ? 'bg-muted/10' : ''}`} onClick={() => setExpandedId(isExpanded ? null : entry.uid)}>
                        <td className="px-4 py-3 font-medium text-foreground">{entry.name}</td>
                        <td className="px-4 py-3 text-muted-foreground">{entry.type}</td>
                        <td className="px-4 py-3 text-muted-foreground">{entry.country}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {entry.programs.slice(0, 2).map(p => (
                              <span key={p} className="px-1.5 py-0.5 rounded bg-secondary text-muted-foreground text-2xs">{p}</span>
                            ))}
                            {entry.programs.length > 2 && <span className="text-2xs text-muted-foreground">+{entry.programs.length - 2}</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground font-tabular">{entry.dateAdded}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-2xs font-semibold ${riskColor[risk]}`}>{risk}</span>
                        </td>
                        <td className="px-4 py-3">
                          <ChevronDown size={13} className={`text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-b border-border bg-muted/20">
                          <td colSpan={7} className="px-4 py-3">
                            <div className="space-y-2">
                              <p className="text-xs text-foreground font-medium">Remarks</p>
                              <p className="text-xs text-muted-foreground leading-relaxed">{entry.remarks}</p>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {entry.programs.map(p => (
                                  <span key={p} className="px-2 py-0.5 rounded bg-secondary text-muted-foreground text-2xs">{p}</span>
                                ))}
                              </div>
                              <p className="text-2xs text-muted-foreground">UID: {entry.uid} · Source: OFAC SDN List (ofac.treasury.gov)</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground text-sm">
                      No entities match your search criteria. <button onClick={clearFilters} className="text-primary hover:underline">Clear filters</button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="p-3 border-t border-border bg-muted/10">
            <p className="text-2xs text-muted-foreground text-center">
              Data sourced from OFAC Specially Designated Nationals (SDN) List · ofac.treasury.gov · Updated {new Date(lastUpdated).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })} · <span className="text-primary">Beta — structured ingestion from public OFAC data</span>
            </p>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
